import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';

import { ChatVerdict } from './chatStream';

type FeedbackButtonsProps = {
  verdict?: ChatVerdict;
  onVerdict: (verdict: ChatVerdict, comment?: string) => void;
};

// Thumbs-up posts on the click. Thumbs-down first offers an optional one-line
// "what went wrong?" and posts once, verdict and comment together: the backend
// de-dups a repeat verdict on (run_id, user, verdict), so a comment sent as a
// second post would be dropped.
function FeedbackButtons({ verdict, onVerdict }: FeedbackButtonsProps) {
  const [asking, setAsking] = useState(false);
  const [comment, setComment] = useState('');

  const dismiss = () => {
    setAsking(false);
    setComment('');
  };

  // Dismissing the input is still a thumbs-down — it just carries no comment.
  const submit = (text: string) => {
    dismiss();
    onVerdict('down', text.trim() || undefined);
  };

  const up = () => {
    dismiss();
    onVerdict('up');
  };

  const down = () => {
    // Backing out of the prompt posts nothing; un-toggling a sent verdict clears it.
    if (asking) dismiss();
    else if (verdict === 'down') onVerdict('down');
    else setAsking(true);
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        <IconButton
          size="small"
          aria-label="Helpful"
          aria-pressed={verdict === 'up'}
          sx={{ opacity: verdict === 'up' ? 1 : 0.5, fontSize: 14 }}
          onClick={up}
        >
          👍
        </IconButton>
        <IconButton
          size="small"
          aria-label="Not helpful"
          aria-pressed={verdict === 'down' || asking}
          sx={{ opacity: verdict === 'down' || asking ? 1 : 0.5, fontSize: 14 }}
          onClick={down}
        >
          👎
        </IconButton>
      </Stack>
      {asking ? (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="What went wrong? (optional)"
            inputProps={{ 'aria-label': 'What went wrong?', maxLength: 280 }}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit(comment);
              }
              // The drawer closes on Esc; in here it only closes this input.
              if (e.key === 'Escape') {
                e.stopPropagation();
                submit('');
              }
            }}
          />
          <Button size="small" data-testid="feedback-comment-send" onClick={() => submit(comment)}>
            Send
          </Button>
          <IconButton size="small" aria-label="Skip comment" onClick={() => submit('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : null}
    </>
  );
}

export default FeedbackButtons;
